import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase/config";

import {
  loginWithGoogle,
  onAuthChange,
} from "../firebase/auth";

import Header from "./components/Header";
import Footer from "./components/Footer";

export default function Register() {
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [connected, setConnected] = useState(false);

  const [username, setUsername] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  /*
   * Check authentication state
   */
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        setFirebaseUser(user);
        setConnected(true);

        setInitializing(false);
      } else {
        setFirebaseUser(null);
        setConnected(false);
        setInitializing(false);
      }
    });

    return () => unsubscribe();
  }, []);

  /*
   * Google login
   */
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await loginWithGoogle();

      if (!result.success) {
        setError("Unable to connect with Google. Please try again.");
      }
    } catch (err) {
      console.error("Google login error:", err);

      setError(
        "Unable to connect with Google. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Create username document
   *
   * usernames/{username}
   *
   * {
   *   active: false,
   *   createdAt: serverTimestamp(),
   *   uid: firebaseUser.uid,
   *   username: username
   * }
   */
  const handleFinish = async () => {
    if (!firebaseUser) {
      setError("Please connect your Google account first.");
      return;
    }

    const normalizedUsername = username
      .trim()
      .toLowerCase();

    /*
     * Basic username validation
     */
    if (normalizedUsername.length < 3) {
      setError(
        "Username must contain at least 3 characters."
      );
      return;
    }

    /*
     * Allow only letters, numbers, underscore and hyphen
     */
    if (!/^[a-z0-9_-]+$/.test(normalizedUsername)) {
      setError(
        "Username can only contain letters, numbers, _ and -."
      );
      return;
    }

    setSaving(true);
    setError("");

    try {
      /*
       * Check if username already exists
       */
      const usernameRef = doc(
        db,
        "usernames",
        normalizedUsername
      );

      const usernameSnapshot = await getDoc(usernameRef);

      if (usernameSnapshot.exists()) {
        setError(
          "This username is already taken. Please choose another one."
        );

        setSaving(false);
        return;
      }

      /*
       * Create username document
       */
      await setDoc(usernameRef, {
        active: false,
        createdAt: serverTimestamp(),
        uid: firebaseUser.uid,
        username: normalizedUsername,
      });

      /*
       * Registration completed
       */
      setUsername(normalizedUsername);
      setDone(true);

    } catch (err) {
      console.error(
        "Error creating username:",
        err
      );

      setError(
        "Unable to create your account. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * Loading
   */
  if (initializing) {
    return (
      <div className="min-h-screen w-full bg-stone-50 flex items-center justify-center">
        <span className="text-sm text-stone-400">
          Loading…
        </span>
      </div>
    );
  }

  /*
   * Success screen
   */
  if (done) {
    return (
      <>
        <Header />

        <div className="min-h-screen w-full bg-stone-50 flex items-center justify-center p-6">
          <div className="max-w-sm w-full text-center">

            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-600 flex items-center justify-center mb-6">
              <Check
                className="text-white"
                size={28}
              />
            </div>

            <h1 className="font-serif text-2xl text-stone-900 mb-2">
              You're all set!
            </h1>

            <p className="text-stone-500 text-sm mb-3 leading-relaxed">
              Your ZeedBio account has been created successfully.
            </p>

            <p className="text-sm text-stone-600 mb-8">
              Your username is{" "}
              <strong className="text-emerald-700">
                @{username}
              </strong>
            </p>

            <button
              onClick={() => navigate("/admin")}
              className="inline-flex items-center gap-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-5 py-2.5 transition-colors"
            >
              Go to dashboard

              <ChevronRight size={16} />
            </button>

          </div>
        </div>

        <Footer />
      </>
    );
  }

  /*
   * Registration
   */
  return (
    <>
      <Header />

      <div className="min-h-screen w-full bg-stone-50 flex justify-center px-4 py-10 md:py-16">

        <div className="w-full max-w-xl">

          <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-10">

            {/* Header */}
            <div className="mb-8">

              <h1 className="font-serif text-2xl text-stone-900">
                Let's create your ZeedBio
              </h1>

              <p className="text-stone-500 mt-2 text-sm leading-relaxed">
                Connect your Google account and choose your ZeedBio username.
              </p>

            </div>

            {!connected ? (

              /*
               * Google login
               */
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-stone-300 rounded-xl px-5 py-3 hover:border-stone-400 hover:bg-stone-50 transition-colors disabled:opacity-50"
              >

                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                >
                  <path
                    fill="#4285F4"
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"
                  />

                  <path
                    fill="#34A853"
                    d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.9v2.33A9 9 0 0 0 9 18z"
                  />

                  <path
                    fill="#FBBC05"
                    d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.9A9 9 0 0 0 0 9c0 1.45.35 2.83.9 4.03l3.05-2.33z"
                  />

                  <path
                    fill="#EA4335"
                    d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .9 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"
                  />
                </svg>

                <span className="text-sm font-medium text-stone-700">
                  {loading
                    ? "Connecting..."
                    : "Continue with Google"}
                </span>

              </button>

            ) : (

              /*
               * Username form
               */
              <div className="space-y-6">

                {/* Connected account */}
                <div className="flex items-center gap-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">

                  <Check size={16} />

                  <span>
                    Connected as{" "}
                    <strong>
                      {firebaseUser?.email ||
                        "Google account"}
                    </strong>
                  </span>

                </div>

                {/* Username */}
                <div>

                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Choose a username
                  </label>

                  <div className="flex items-center border border-stone-300 rounded-xl px-4 focus-within:border-emerald-600 transition-colors">

                    <span className="text-stone-400 text-sm">
                      @
                    </span>

                    <input
                      type="text"
                      value={username}
                      onChange={(e) => {
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/\s/g, "");

                        setUsername(value);

                        /*
                         * Clear previous error
                         * while typing
                         */
                        if (error) {
                          setError("");
                        }
                      }}
                      placeholder="yourbusiness"
                      maxLength={30}
                      autoComplete="off"
                      className="w-full py-3 px-2 text-sm text-stone-900 outline-none bg-transparent"
                    />

                  </div>

                  <p className="text-xs text-stone-400 mt-2">
                    At least 3 characters. Use letters,
                    numbers, _ or -.
                  </p>

                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <p className="text-sm text-red-600">
                      {error}
                    </p>
                  </div>
                )}

                {/* Create */}
                <div className="flex justify-end pt-6 border-t border-stone-100">

                  <button
                    onClick={handleFinish}
                    disabled={
                      saving ||
                      username.trim().length < 3
                    }
                    className={
                      "flex items-center gap-1 text-sm px-5 py-2.5 rounded-full font-medium transition-colors " +
                      (
                        username.trim().length >= 3 &&
                        !saving
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : "bg-stone-200 text-stone-400 cursor-not-allowed"
                      )
                    }
                  >

                    {saving
                      ? "Creating..."
                      : "Create account"}

                    {!saving && (
                      <ChevronRight size={16} />
                    )}

                  </button>

                </div>

              </div>
            )}

            {/* Login error */}
            {error && !connected && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mt-4">
                <p className="text-sm text-red-600">
                  {error}
                </p>
              </div>
            )}

          </div>

        </div>

      </div>

      <Footer />
    </>
  );
}

